import supabase from '@/lib/supabaseClient';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').trim();

// Local-dev convenience only -- must never resolve to a backend URL for any
// non-localhost hostname, or a misconfigured REACT_APP_API_BASE_URL on a
// real deploy (staging or production) would silently route live traffic to
// the dev Railway backend instead of failing loudly.
function getFallbackApiBase() {
  if (typeof window === 'undefined') return '';

  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8001';
  }

  return '';
}

async function parseJson(response) {
  return response.json().catch(() => ({ detail: 'An error occurred' }));
}

export async function apiCall(endpoint, options = {}) {
  // Get the current session's access token for authenticated API calls
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const traceId = `cp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  // Keep the trace ID client-side. Do not send a new custom header here:
  // that would trigger a CORS preflight which the staging API does not allow.
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const requestOptions = {
    ...options,
    headers,
  };

  let response;

  try {
    response = await fetch(`${API_BASE}${endpoint}`, requestOptions);
  } catch (error) {
    if (API_BASE) throw error;

    const fallbackApiBase = getFallbackApiBase();
    if (!fallbackApiBase) throw error;

    response = await fetch(`${fallbackApiBase}${endpoint}`, requestOptions);
  }

  if (!response.ok && !API_BASE && response.status === 404) {
    const fallbackApiBase = getFallbackApiBase();
    if (fallbackApiBase) {
      const fallbackResponse = await fetch(`${fallbackApiBase}${endpoint}`, requestOptions);
      if (fallbackResponse.ok) {
        return fallbackResponse.json();
      }
      response = fallbackResponse;
    }
  }

  if (!response.ok) {
    const error = await parseJson(response);
    const detail = error.detail;
    const detailMessage = Array.isArray(detail)
      ? detail.map(e => {
          const path = Array.isArray(e.loc) ? e.loc.filter(Boolean).join('.') : '';
          return path ? `${path}: ${e.msg || 'Invalid value'}` : (e.msg || JSON.stringify(e));
        }).join('; ')
      : (detail || `HTTP error ${response.status}`);
    const message = `${response.status} ${options.method || 'GET'} ${endpoint}: ${detailMessage} (trace ${traceId})`;
    const failure = new Error(message);
    failure.status = response.status;
    failure.endpoint = endpoint;
    failure.traceId = traceId;
    failure.responseDetail = detail;
    throw failure;
  }
  return response.json();
}

export const companiesHouseApi = {
  search: (query, limit = 8) =>
    apiCall(`/api/companies-house/search?${new URLSearchParams({ q: query, limit: String(limit) })}`),

  // Sign-up form: nobody is signed in yet, so this is the rate-limited public variant.
  publicSearch: (query) =>
    apiCall(`/api/companies-house/public-search?${new URLSearchParams({ q: query })}`),

  getProfile: (companyNumber) =>
    apiCall(`/api/companies-house/company/${companyNumber}`),
};

export const stripeApi = {
  createCheckoutSession: (data) =>
    apiCall('/api/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  verifySession: (sessionId) =>
    apiCall(`/api/stripe/verify-session?${new URLSearchParams({ session_id: sessionId })}`),
};

// Gateway-agnostic checkout. The server says which gateways the user's region offers (primary first).
// If the primary fails to create a checkout nothing was charged, so the next gateway is tried; a 409
// (already paid) is never retried.
export const paymentsApi = {
  options: () => apiCall('/api/payments/options'),

  async startCheckout(data) {
    let providers = ['stripe'];
    try {
      providers = (await apiCall('/api/payments/options')).providers || providers;
    } catch (err) {
      // Options unavailable: fall back to the original Stripe flow.
    }
    let lastError;
    for (const provider of providers) {
      try {
        const result = provider === 'hitpay'
          ? await apiCall('/api/payments/hitpay/create-checkout', { method: 'POST', body: JSON.stringify(data) })
          : await stripeApi.createCheckoutSession(data);
        return { ...result, provider };
      } catch (err) {
        // Only 424 means the server confirms no checkout exists for this gateway (502/503 never reach the browser
        // intact: the hosting edge replaces them with a page that has no CORS headers). Anything else
        // (409 already paid, 429 double-click, 500, ...) may have left a payable link, so never retry elsewhere.
        if (err.status === 424) {
          lastError = err;
          continue;
        }
        // The fallback gateway itself failing (a host-hidden 5xx shows up as a status-less network error) after the
        // primary confirmed it created nothing: both gateways are down, so report that rather than a raw fetch error.
        if (lastError && (err.status === undefined || err.status >= 500)) {
          lastError = err;
          break;
        }
        throw err;
      }
    }
    // Every gateway failed before a checkout existed (or the host hid the status): say so plainly, keep the status.
    const friendly = new Error('Payment service temporarily unavailable. Please try again in a moment or contact support.');
    friendly.status = lastError && lastError.status;
    throw friendly;
  },

  // Confirms a payment on return from either gateway; `params` is the page's URLSearchParams.
  verifyReturn(params) {
    const hitpayRef = params.get('pay_ref');
    if (hitpayRef) {
      return apiCall(`/api/payments/hitpay/verify?${new URLSearchParams({ pay_ref: hitpayRef })}`);
    }
    return stripeApi.verifySession(params.get('stripe_session_id'));
  },
};

export const pricingApi = {
  // The price of an order as it would be invoiced: per company, with the volume discount and the total.
  quote: (payload) =>
    apiCall('/api/pricing/quote', { method: 'POST', body: JSON.stringify(payload) }),
};

export const wizardApi = {
  notifyFileUploaded: (companyId, companyName, fileName, fileSize) =>
    apiCall('/api/wizard/file-uploaded', {
      method: 'POST',
      body: JSON.stringify({
        company_id: companyId,
        company_name: companyName,
        file_name: fileName,
        file_size: fileSize,
      }),
    }),

  submitFeedback: (stars, comment, jobIds) =>
    apiCall('/api/wizard/feedback', {
      method: 'POST',
      body: JSON.stringify({ stars, comment: comment || null, job_ids: jobIds || null }),
    }),
};

export const jobsApi = {
  createJobs: (data) =>
    apiCall('/api/jobs/create', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listServices: (jobUuid) =>
    apiCall(`/api/jobs/${encodeURIComponent(jobUuid)}/services`),

  // The client approves one service (the Tax Computation) against the exact version they downloaded.
  approveService: (jobUuid, serviceKey, packageHash) =>
    apiCall(`/api/jobs/${encodeURIComponent(jobUuid)}/services/${encodeURIComponent(serviceKey)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ package_hash: packageHash }),
    }),

  downloadServicePackage: async (jobUuid, serviceKey) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const base = (process.env.REACT_APP_API_BASE_URL || '').trim() || getFallbackApiBase();
    const response = await fetch(
      `${base}/api/jobs/${encodeURIComponent(jobUuid)}/services/${encodeURIComponent(serviceKey)}/package`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      const error = await parseJson(response);
      throw new Error(error.detail || `HTTP error ${response.status}`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return {
      blob,
      filename: match ? match[1] : `${serviceKey}.zip`,
      // The hash an approval is bound to: it is only known to the client by having been shown this file.
      packageHash: response.headers.get('x-package-hash') || '',
    };
  },

  approveReview: (jobId) =>
    apiCall(`/api/jobs/${encodeURIComponent(jobId)}/approve-review`, {
      method: 'POST',
    }),

  requestChanges: (jobId, comment) =>
    apiCall(`/api/jobs/${encodeURIComponent(jobId)}/request-changes`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),

  rateJob: (jobId, stars, comment) =>
    apiCall(`/api/jobs/${encodeURIComponent(jobId)}/rate`, {
      method: 'POST',
      body: JSON.stringify({ stars, comment: comment || null }),
    }),

  downloadIxbrlPackage: async (jobId) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const base = (process.env.REACT_APP_API_BASE_URL || '').trim() || getFallbackApiBase();
    const response = await fetch(`${base}/api/jobs/${encodeURIComponent(jobId)}/ixbrl-download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const error = await parseJson(response);
      throw new Error(error.detail || `HTTP error ${response.status}`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob, filename: match ? match[1] : `ixbrl_package_${jobId}.zip` };
  },
};

export const invoicesApi = {
  list: (limit = 20, offset = 0) =>
    apiCall(`/api/invoices?limit=${limit}&offset=${offset}`),
  get: (invoiceId) =>
    apiCall(`/api/invoices/${invoiceId}`),
};
